import { Icon, Image } from '@acme/assets';
import type { FC } from 'react';

export interface FileAttachmentItem {
  id?: string;
  fileName?: string | null;
  size?: string | null;
}

export interface FileAttachmentProps {
  fileName?: string | null;
  size?: string | null;
  className?: string;
  onClick?: () => void;
}

export interface FileAttachmentListProps {
  attachments?: FileAttachmentItem[] | null;
  onDownload: (id: string, fileName: string) => void;
  className?: string;
  title?: string | null;
}

const getFileTypeText = (fileName?: string | null) => {
  const safe = (fileName || '').trim();
  const lastDot = safe.lastIndexOf('.');
  if (lastDot === -1 || lastDot === safe.length - 1) {
    return 'FILE';
  }
  return safe.slice(lastDot + 1).toLocaleUpperCase();
};

const getFileNameText = (fileName?: string | null) => {
  const safe = (fileName || '').trim();
  const lastDot = safe.lastIndexOf('.');
  if (lastDot <= 0) {
    return safe;
  }
  return safe.slice(0, lastDot);
};

export const FileAttachment: FC<FileAttachmentProps> = ({
  fileName,
  size,
  className = '',
  onClick,
}) => {
  const nameText = getFileNameText(fileName) || 'Вложение';
  const typeText = getFileTypeText(fileName);

  return (
    <div className="p-4 bg-gray-100 rounded-r-md rounded-bl-md">
      <div
        className={`flex items-center p-2 gap-4 rounded-lg bg-gray-50 ${className}`}
      >
        <div className="flex flex-1 flex-col gap-2 min-w-0">
          <div className="flex gap-2 min-w-0">
            <div className="text-lg shrink-0">
              <Image name="pdf" className="h-5 w-5" alt="File" />
            </div>
            <div className="text-sm font-medium truncate">{nameText}</div>
          </div>
          <div className="text-xs font-normal flex text-gray-500 items-center mt-2">
            <span>{typeText}</span>
            {size && (
              <>
                <span>•</span>
                <span>{size}</span>
              </>
            )}
          </div>
        </div>
        {onClick && (
          <button
            type="button"
            onClick={onClick}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Скачать"
          >
            <Icon name="download-outline" size={16} alt="Скачать" />
          </button>
        )}
      </div>
    </div>
  );
};

export const FileAttachmentList: FC<FileAttachmentListProps> = ({
  attachments,
  onDownload,
  className = '',
}) => {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="text-normal text-sm text-gray-500 mb-3 mt-6">
        Прикрепленные файлы
      </div>
      <div className="flex gap-4">
        {attachments.map((attachment, idx) => (
          <FileAttachment
            key={attachment.id || `${attachment.fileName || 'file'}-${idx}`}
            fileName={attachment.fileName}
            size={attachment.size}
            onClick={() =>
              onDownload(attachment.id || '', attachment.fileName || '')
            }
          />
        ))}
      </div>
    </div>
  );
};
